import "./Chat.scss";

import React, { useCallback, useEffect, useRef } from "react";
import { animateScroll as scroll } from "react-scroll";

import type { MessagesState, MessageType } from "../../types";
import { ErrorMessage } from "./ErrorMessage";
import { FunctionMessage } from "./FunctionMessage";
import { Message } from "./Message";

interface ChatProps {
  messages: MessagesState;
  isLoading: boolean;
  disabledSubmit?: boolean;
  deleteMessage: (id: number | string) => void;
  editMessage: (message: MessageType) => void;
  sendQuestion: (question: string | null) => void;
}

const Chat: React.FC<ChatProps> = ({
  messages,
  isLoading,
  disabledSubmit,
  deleteMessage,
  editMessage,
  sendQuestion,
}) => {
  const isProgrammaticScroll = useRef(false);
  const timerId = useRef<number>(-1);

  const dropScroll = useCallback(() => {
    clearTimeout(timerId.current);
    isProgrammaticScroll.current = false;
  }, []);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        isProgrammaticScroll.current = true;
      }, 100);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isProgrammaticScroll.current) {
      timerId.current = window.setTimeout(() => {
        scroll.scrollToBottom({
          containerId: "chat-container",
          duration: 200,
          smooth: true,
          ignoreCancelEvents: false,
        });
      }, 300);
    }
  }, [messages]);

  return (
    <div
      id="chat-container"
      className="playground-chat"
      onWheel={dropScroll}
      onTouchMove={dropScroll}
      data-testid="chat-container"
    >
      {Object.values(messages)
        .filter(
          ({ choices, selectedChoice }) =>
            choices[selectedChoice].role !== "system",
        )
        .map((message: MessageType) => {
          if (message.error) {
            return (
              <ErrorMessage
                deleteMessage={deleteMessage}
                key={message.id}
                id={message.id}
                status={message.error.status}
                latency={message.headers?.latency}
              />
            );
          }

          if (message.type === "function") {
            return (
              <FunctionMessage
                message={message}
                key={message.id}
                isLoading={isLoading}
                messagesList={messages}
                deleteMessage={deleteMessage}
                editMessage={editMessage}
                sendQuestion={sendQuestion}
                disabledSubmit={disabledSubmit}
              />
            );
          }

          if (message.choices[message.selectedChoice].role === "tool") {
            return null;
          }

          return (
            <Message
              message={message}
              key={message.id}
              deleteMessage={deleteMessage}
              editMessage={editMessage}
              isLoading={isLoading}
            />
          );
        })}
    </div>
  );
};

export { Chat };
